/**
 * Detect whether a rear (environment-facing) camera can be used for capture.
 *
 * `<input capture="environment">` only *hints* at the rear camera; on desktops
 * and devices without one, browsers fall back to the file picker. We probe
 * with getUserMedia({ facingMode: { exact: 'environment' } }) so Take photo
 * can be disabled instead of silently opening the gallery.
 */

export const ENVIRONMENT_CAMERA_UNAVAILABLE = {
  unsupported: 'Camera capture is not supported in this browser.',
  insecure: 'Camera requires a secure (HTTPS) connection.',
  none: 'No rear camera available on this device.',
  denied: 'Camera access was denied. Allow camera permission to take photos.',
};

let cachedResult = null;
let inflight = null;

const REAR_LABEL = /back|rear|environment|ultra.?wide|telephoto/i;

/**
 * @returns {Promise<{ available: boolean, reason: string|null }>}
 */
export async function checkEnvironmentCameraSupport() {
  if (cachedResult) return cachedResult;
  if (inflight) return inflight;

  inflight = probeEnvironmentCamera()
    .then((result) => {
      cachedResult = result;
      inflight = null;
      return result;
    })
    .catch(() => {
      const result = { available: false, reason: ENVIRONMENT_CAMERA_UNAVAILABLE.none };
      cachedResult = result;
      inflight = null;
      return result;
    });

  return inflight;
}

/** Clear cached result (e.g. after the user changes site permissions). */
export function clearEnvironmentCameraSupportCache() {
  cachedResult = null;
  inflight = null;
}

async function probeEnvironmentCamera() {
  if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return { available: false, reason: ENVIRONMENT_CAMERA_UNAVAILABLE.unsupported };
  }

  if (!window.isSecureContext) {
    return { available: false, reason: ENVIRONMENT_CAMERA_UNAVAILABLE.insecure };
  }

  try {
    if (navigator.mediaDevices.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === 'videoinput');

      if (videoInputs.length === 0) {
        return { available: false, reason: ENVIRONMENT_CAMERA_UNAVAILABLE.none };
      }

      const labeled = videoInputs.filter((d) => d.label);
      if (labeled.length > 0) {
        // Labels are only present after prior permission; use them to avoid re-prompting.
        if (labeled.some((d) => REAR_LABEL.test(d.label))) {
          return { available: true, reason: null };
        }
        // Desktop webcam / front-only — capture="environment" would open the file picker.
        return { available: false, reason: ENVIRONMENT_CAMERA_UNAVAILABLE.none };
      }
    }
  } catch {
    // Fall through to getUserMedia probe.
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: 'environment' } },
    });
    const facing = stream.getVideoTracks()[0]?.getSettings?.()?.facingMode;
    stream.getTracks().forEach((track) => track.stop());

    if (facing === 'user') {
      return { available: false, reason: ENVIRONMENT_CAMERA_UNAVAILABLE.none };
    }
    return { available: true, reason: null };
  } catch (err) {
    const name = err?.name || '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return { available: false, reason: ENVIRONMENT_CAMERA_UNAVAILABLE.denied };
    }
    // OverconstrainedError / NotFoundError / NotReadableError → no usable rear camera
    return { available: false, reason: ENVIRONMENT_CAMERA_UNAVAILABLE.none };
  }
}
